import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../App';
import { getEventsCached, invalidateCalendarCache } from '../lib/calendarCache';
import { parseFOHMeta } from '../api/rules';
import {
  toLocalDateStr, startOfWeek, nextStartOfWeek,
  fmtDateRange, fmtTimeCompact, fmtDayDate, eventEndDate, eventOverlapsDate, eventDates, parseClockTime,
} from '../lib/datetime';
import { isAssignedTo, getEventAssignee, stripAssigneeTag } from '../api/assignments';
import { pickupsForDate, overrideKey } from '../lib/pickups';
import { fsGetCol, reconnectGCal } from '../lib/firebase';
import { GCalAuthError } from '../lib/gcal';
import { assignCalendarColors } from '../lib/color';
import { checkScheduleCatchup } from '../api/schedulePublish';
import LogisticsModule  from '../logistics/LogisticsModule';
import SettingsModule   from '../settings/SettingsModule';
import OwnerSchoolModule from '../school/OwnerSchoolModule';
import CoverageRequestSheet from '../components/CoverageRequestSheet';
import { CoverageIcon, ReminderIcon } from '../components/ui/icons';
import GCalReconnectPrompt from '../components/ui/GCalReconnectPrompt';
import AccountMenu from '../components/ui/AccountMenu';

// ─── HOME SCREEN ──────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: 'schedule', label: 'Add to Schedule', emoji: '📅' },
];

// How far ahead "Coming Up" (and the pickup-override lookup) reaches.
const MAX_WINDOW_DAYS = 14;

// Resolve the current user's care-partner identity + calendar mapping.
// Shared by MyPersonalView and WeekSummaryCard so "mine" means the same thing everywhere.
function useMyIdentity() {
  const { currentUser, familyData } = useApp() || {};

  const me = (familyData?.carePartners || []).find(
    cp => cp.email?.toLowerCase() === currentUser?.email?.toLowerCase()
  );
  const myId   = me?.identifier || null;
  const myName = me?.name || currentUser?.name || null;

  const calAssignments = familyData?.config?.calendarAssignments || {};
  const myCalIds = Object.entries(calAssignments)
    .filter(([, n]) => n?.toLowerCase() === myName?.toLowerCase())
    .map(([id]) => id);
  const allCalIds = Object.keys(calAssignments);

  return { myId, myName, myCalIds, allCalIds };
}

// Fetches every assigned calendar ONCE per Home mount, in parallel, over a
// window wide enough for both Today/Coming Up (today → today+14) and the
// This Week / Next Week cards (Monday-of-this-week → next Sunday, which can
// start before "today"). MyPersonalView and both WeekSummaryCards derive
// their own filtered views from this shared, deduped event list instead of
// each firing their own fetchEvents loop. Results are cached (see
// lib/calendarCache) so remounting Home within the TTL costs no network call.
function useHomeCalendarData() {
  const { familyData } = useApp() || {};
  const [rawEvents, setRawEvents] = useState([]);
  const [ignoredMap, setIgnoredMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authRequired, setAuthRequired] = useState(false);

  const calIds = Object.keys(familyData?.config?.calendarAssignments || {});
  const calIdsKey = calIds.join(',');

  const load = useCallback(async () => {
    if (!familyData) { setLoading(false); return; }
    if (calIds.length === 0) { setRawEvents([]); setIgnoredMap({}); setLoading(false); return; }

    setLoading(true);
    setError(null);
    setAuthRequired(false);
    try {
      const now      = new Date();
      const winStart = startOfWeek(now);
      const winEnd   = new Date(now); winEnd.setHours(0, 0, 0, 0);
      winEnd.setDate(winEnd.getDate() + MAX_WINDOW_DAYS);
      winEnd.setHours(23, 59, 59, 999);

      let calAuthRequired = false;
      const perCal = await Promise.all(calIds.map(async calId => {
        try {
          const evts = await getEventsCached({
            calendarId: calId,
            timeMin: winStart.toISOString(),
            timeMax: winEnd.toISOString(),
            maxResults: 200,
          });
          return (evts || []).map(e => ({ ...e, _calendarId: calId }));
        } catch (err) {
          console.warn('[useHomeCalendarData] cal fetch failed:', calId, err.message);
          if (err instanceof GCalAuthError) calAuthRequired = true;
          return [];
        }
      }));
      if (calAuthRequired) setAuthRequired(true);

      const seen = new Set();
      const deduped = [];
      for (const e of perCal.flat()) {
        if (seen.has(e.id)) continue;
        seen.add(e.id);
        deduped.push(e);
      }
      setRawEvents(deduped);

      let ignored = {};
      if (familyData?.id) {
        try {
          const rows = await fsGetCol(`families/${familyData.id}/ignoredEvents`);
          for (const i of (rows || [])) ignored[i.id] = i;
        } catch (err) {
          console.warn('[useHomeCalendarData] ignoredEvents fetch failed:', err.message);
        }
      }
      setIgnoredMap(ignored);
    } catch (err) {
      console.error('[useHomeCalendarData]', err);
      setError('Could not load your calendar. Connect calendars in Settings.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyData, calIdsKey]);

  useEffect(() => { load(); }, [load]);

  return { rawEvents, ignoredMap, loading, error, authRequired, reload: load };
}

export default function HomeScreen() {
  const { familyData } = useApp() || {};
  const [activeModule, setActiveModule] = useState(null);
  const [reviewFilter, setReviewFilter] = useState(null);
  const [reviewWeek, setReviewWeek] = useState('this');
  const homeCal = useHomeCalendarData();

  // Recovers a batched caregiver-schedule publish that never flushed (tab
  // closed mid-window, etc.) — see api/schedulePublish.js. Keyed on family id
  // only, not the whole familyData object, so it runs once per session/family
  // rather than on every incidental familyData patch elsewhere in the app.
  useEffect(() => {
    if (familyData?.id) checkScheduleCatchup(familyData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyData?.id]);

  const openReview = (filter = null, week = 'this') => { setReviewFilter(filter); setReviewWeek(week); setActiveModule('review_schedule'); };
  // Review & Add-to-Schedule can mutate GCal events directly (assign, lock in,
  // create) without touching calendarAssignments, so the shared cache won't
  // auto-invalidate on its own — bust it on the way back to Home.
  const closeReviewToHome = () => { invalidateCalendarCache(); setActiveModule(null); };

  const openSettings = () => setActiveModule('settings');
  const hasHomeschoolStudent = (familyData?.children || []).some(c => c.schoolType === 'homeschool' && c.studentEmail);
  const navItems = hasHomeschoolStudent ? [...NAV_ITEMS, { id: 'school', label: 'School', emoji: '🎓' }] : NAV_ITEMS;

  if (activeModule === 'review_schedule')  return <LogisticsModule onBack={closeReviewToHome} onSettings={openSettings} initialView="review" initialReviewFilter={reviewFilter} initialWeek={reviewWeek} />;
  if (activeModule === 'schedule')         return <LogisticsModule onBack={closeReviewToHome} onSettings={openSettings} initialView="schedule" />;
  if (activeModule === 'settings')         return <SettingsModule  onBack={() => setActiveModule(null)} />;
  if (activeModule === 'settings/general') return <SettingsModule  onBack={() => setActiveModule(null)} initialSection="general" />;
  if (activeModule === 'school')           return <OwnerSchoolModule onBack={() => setActiveModule(null)} />;

  async function handleReconnect() {
    try {
      await reconnectGCal();
      homeCal.reload();
    } catch (err) {
      console.error('[FOH] reconnect failed:', err);
    }
  }

  return (
    <div className="home-screen">
      {/* Header — includes cash indicator + settings gear */}
      <HomeHeader
        onSettings={() => setActiveModule('settings')}
        onCash={() => setActiveModule('settings/general')}
      />

      {homeCal.authRequired && <GCalReconnectPrompt onReconnect={handleReconnect} />}

      {/* Main column (Today/Coming Up) + side rail (week status + nav) —
          side-by-side at wide viewports (see .home-main-rail), stacked
          on phones exactly as before. */}
      <div className="home-main-rail">
        <div className="home-main-col">
          {/* Your Schedule — logged-in user's personal view */}
          <div className="schedule-section">
            <div className="schedule-section__header">
              <span className="schedule-section__title">Your Schedule</span>
            </div>
            <MyPersonalView onReview={() => openReview()} homeCal={homeCal} />
          </div>
        </div>

        <div className="home-side-rail">
          {/* Family planning — this week / next week, hard Mon–Sun */}
          <div className="schedule-section">
            <div className="schedule-section__header">
              <span className="schedule-section__subtitle">Family Logistics · Mon–Sun</span>
            </div>
            <ReviewScheduleBar onTap={openReview} homeCal={homeCal} />
          </div>

          {/* Nav */}
          <div className="nav-grid">
            {navItems.map(item => (
              <button
                key={item.id}
                className="nav-button"
                onClick={() => setActiveModule(item.id)}
              >
                <span className="nav-button__emoji">{item.emoji}</span>
                <span className="nav-button__label">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HEADER ───────────────────────────────────────────────────────────────────

function HomeHeader({ onSettings, onCash }) {
  const { familyData } = useApp() || {};

  return (
    <div className="home-header">
      <div className="home-header__brand">
        <div className="home-header__glyph">
          <img src="/grove-symbol.png" alt="" />
        </div>
        <span className="home-header__wordmark">
          Grove <span className="home-header__dot">&middot;</span> <span className="home-header__subtitle">Family Ops Hub</span>
        </span>
      </div>

      {/* Cash indicator inline — pipe separated. Feature-flagged off until money management ships. */}
      {familyData?.config?.features?.cashSignal && (
        <>
          <span className="home-header__pipe">|</span>
          <CashSignal onTap={onCash} familyData={familyData} />
        </>
      )}

      <AccountMenu onSettings={onSettings} />
    </div>
  );
}

// ── CASH SIGNAL ──────────────────────────────────────────────────────────────
// Dollar-sign signal bars: $$$$ = healthy, $ = low
// Inspired by cell signal — 4 bars, bold+colored by balance level

function CashSignal({ onTap, familyData }) {
  const balance   = familyData?.config?.cashOnHand   ?? 0;
  const threshold = familyData?.config?.cashThreshold ?? 50;

  const level = balance <= 0 ? 0
    : balance < threshold        ? 1
    : balance < threshold * 2    ? 2
    : balance < threshold * 4    ? 3
    : 4;

  // Active colour per level — all dark enough for contrast on #D4C9BE sand
  const activeColor =
    level === 0 ? '#7A2E2E' :   // deep earthy red  (empty)
    level === 1 ? '#8B4A1E' :   // burnt sienna      (critical)
    level === 2 ? '#8B6030' :   // dark amber-bark   (low)
    '#3A4039';                   // forest-dark       (healthy)

  // Inactive bars: medium sage — clearly muted but legible on sand
  const inactiveColor = '#8D9986';  // --sage

  return (
    <button className="cash-signal" onClick={onTap} title={`Cash on hand: $${balance}`}>
      {[0,1,2,3].map(i => (
        <span key={i} className="cash-signal__bar"
          style={{
            color:      i < level ? activeColor : inactiveColor,
            fontWeight: i < level ? 800 : 400,
            opacity:    i < level ? 1 : 0.45,
          }}>
          $
        </span>
      ))}
    </button>
  );
}

// ─── MY PERSONAL VIEW — the "for me" section ──────────────────────────────────
// Shows Today's events and a rolling "Coming Up" window filtered to the logged-in
// person. "Mine" = personal calendar OR assigned via FOH tag/identifier.
// Family events that aren't mine are kept (not discarded) so empty states can
// show what's actually on the calendar instead of just "Clear".

function MyPersonalView({ onReview, homeCal }) {
  const { familyData } = useApp() || {};
  const { myId, myName, myCalIds, allCalIds } = useMyIdentity();
  const { rawEvents: allCalEvents, ignoredMap, loading: calLoading, error: calError, reload: reloadCal } = homeCal;
  const [todayEvents,    setTodayEvents]    = useState([]);
  const [familyToday,    setFamilyToday]    = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [familyUpcoming, setFamilyUpcoming] = useState([]);
  const [todayView, setTodayView] = useState('me');  // 'me' | 'family'
  const [weekView,  setWeekView]  = useState('me');
  const [pickupsLoading, setPickupsLoading] = useState(true);
  const [pickupsError,   setPickupsError]   = useState(null);

  const loading = calLoading || pickupsLoading;
  const error   = calError || pickupsError;

  // Re-derives "mine" vs. family + pickup rows whenever the shared calendar
  // fetch (homeCal) lands. Only pickups (Firestore, not GCal) are fetched here.
  const derive = useCallback(async () => {
    if (!familyData) { setPickupsLoading(false); return; }
    // If we can't identify this user, show empty but don't error
    if (!myId && myCalIds.length === 0) { setPickupsLoading(false); return; }
    if (calLoading) return;
    if (calError) { setPickupsError(null); setPickupsLoading(false); return; }

    const calIds = allCalIds.length > 0 ? allCalIds : (myCalIds.length > 0 ? myCalIds : []);
    if (calIds.length === 0) { setPickupsLoading(false); return; }

    setPickupsLoading(true);
    setPickupsError(null);

    try {
      const now    = new Date();
      const today  = toLocalDateStr(now);
      const rStart = new Date(now); rStart.setHours(0, 0, 0, 0);
      const rEnd   = new Date(rStart); rEnd.setDate(rEnd.getDate() + MAX_WINDOW_DAYS); rEnd.setHours(23, 59, 59, 999);

      const calEvents = allCalEvents.filter(e =>
        !(ignoredMap[e.id] || (e.recurringEventId && ignoredMap[e.recurringEventId])));

      // Calendar id -> { personName, color }, so every row consistently shows
      // whose calendar an event lives on — including your own, so "which
      // calendar is this on" always has the same answer instead of only
      // appearing sometimes. Colors are re-mapped onto a small curated Grove
      // palette (nearest-hue match, no repeats among the family's own
      // calendars) rather than using the raw GCal hex directly, so calendars
      // stay visually distinct from each other and always legible, instead
      // of two calendars looking near-identical or a pastel one vanishing.
      const calListWithMeta = familyData?.config?.calendarMeta || [];
      const calMetaById = {};
      for (const m of calListWithMeta) {
        if (m?.id) calMetaById[m.id] = m;
      }
      const calColorById = assignCalendarColors(
        calListWithMeta.filter(m => m?.personName).map(m => ({ id: m.id, color: m.color }))
      );
      const withCalTag = e => {
        const m = e._calendarId ? calMetaById[e._calendarId] : null;
        const person = m?.personName;
        if (!person) return e;
        // "Katy McDonald" -> "Katy" — a short-name reads cleanly in the row's
        // tight space; full names were relying on CSS ellipsis truncation.
        const shortName = person.trim().split(/\s+/)[0];
        const color = calColorById[e._calendarId] || null;
        return { ...e, _calPersonName: shortName, _calColor: color };
      };
      const calEventsTagged = calEvents.map(withCalTag);

      // "Mine" = on one of my personal calendars OR assigned to my identifier in FOH block OR tagged in title
      const mine = calEventsTagged.filter(e => isAssignedTo(e, { identifier: myId, calIds: myCalIds }));
      const notMine = calEventsTagged.filter(e => !isAssignedTo(e, { identifier: myId, calIds: myCalIds }));

      // Calendar id → care-partner identifier, so calendar-only family events
      // still get an assignee letter (getEventAssignee only reads FOH tags).
      const calIdToAssignee = {};
      for (const [calId, name] of Object.entries(familyData?.config?.calendarAssignments || {})) {
        const cp = (familyData?.carePartners || []).find(c => c.name?.toLowerCase() === name?.toLowerCase());
        if (cp?.identifier) calIdToAssignee[calId] = cp.identifier;
      }

      // Pickup responsibilities, as synthetic event rows that sort inline.
      // Mine go into my lists; other partners' feed the Family view.
      const familyPickups = [];
      if (myId) {
        let overridesMap = {};
        if (familyData?.id) {
          try {
            const rStartStr = toLocalDateStr(rStart);
            const rEndStr   = toLocalDateStr(rEnd);
            const overrides = await fsGetCol(`families/${familyData.id}/pickupOverrides`, [
              { field: 'date', op: '>=', value: rStartStr },
            ]);
            for (const o of (overrides || [])) {
              if (o.date > rEndStr) continue;
              overridesMap[overrideKey(o.childId, o.date, o.routineId, o.leg)] = o;
            }
          } catch (err) {
            console.warn('[MyPersonalView] pickup overrides fetch failed:', err.message);
          }
        }
        for (const d = new Date(rStart); d <= rEnd; d.setDate(d.getDate() + 1)) {
          const dateStr = toLocalDateStr(d);
          for (const p of pickupsForDate(familyData?.children || [], dateStr, overridesMap, { active: familyData?.config?.pickupsActive !== false })) {
            const isMine = p.adultId === myId;
            // Family view only shows partner pickups — unset or sitter ('cg:') ones belong to Review
            const isPartner = !isMine && p.adultId && !String(p.adultId).startsWith('cg:');
            if (!isMine && !isPartner) continue;
            const hm = parseClockTime(p.time);
            const pickupEvent = {
              id: `pickup_${p.key}`,
              summary: `${p.leg === 'dropoff' ? '🎒' : '🚗'} ${p.label} · ${p.childName}`,
              start: hm
                ? { dateTime: `${dateStr}T${String(hm.h).padStart(2, '0')}:${String(hm.m).padStart(2, '0')}:00` }
                : { date: dateStr },
              _pickupChildId: p.childId,
              _pickupDate: dateStr,
            };
            if (isMine) mine.push(pickupEvent);
            else familyPickups.push({ ...pickupEvent, _assignee: p.adultId });
          }
        }
      }

      const byStart = (a, b) =>
        (a.start?.dateTime || a.start?.date || '').localeCompare(
         (b.start?.dateTime || b.start?.date || ''));
      mine.sort(byStart);

      // "Today" uses overlap (not equality) so a multi-day event that started
      // earlier keeps showing up through its last day, not just its first.
      setTodayEvents(mine.filter(e => eventOverlapsDate(e, today)));
      // "Coming Up" = mine, with at least one day still ahead, out to the max
      // window (client-filtered by the toggle below). Uses end date, not
      // start date, so a multi-day event that's already underway still shows
      // its remaining days here — Today covers today's slice, this covers
      // what's left after today.
      setUpcomingEvents(mine.filter(e => eventEndDate(e) > today));

      const familyAll = notMine
        .map(e => ({ ...e, _assignee: getEventAssignee(e) || calIdToAssignee[e._calendarId] || null }))
        .concat(familyPickups);
      setFamilyToday(familyAll.filter(e => eventOverlapsDate(e, today)).sort(byStart));
      setFamilyUpcoming(familyAll.filter(e => eventEndDate(e) > today).sort(byStart));
    } catch (err) {
      console.error('[MyPersonalView]', err);
      setPickupsError('Could not load your calendar. Connect calendars in Settings.');
    } finally {
      setPickupsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyData, myId, myName, JSON.stringify(myCalIds), JSON.stringify(allCalIds), allCalEvents, ignoredMap, calLoading, calError]);

  useEffect(() => { derive(); }, [derive]);

  // Coverage-request accept / running-late flows mutate GCal events; bust the
  // shared cache and re-pull both calendar + pickup data so Home reflects it.
  const onDataChanged = useCallback(() => {
    invalidateCalendarCache();
    reloadCal();
  }, [reloadCal]);

  const cap = familyData?.config?.homeItemLimit ?? 5;

  // Don't render at all if user can't be identified and calendars aren't set up
  if (!loading && !error && !myId && myCalIds.length === 0) {
    return (
      <div className="my-day-panels">
        <div className="my-panel my-panel--empty">
          <p className="my-panel__empty">
            Set up your calendar connections in Settings to see your personal view.
          </p>
          <button className="btn btn--sm btn--link" onClick={onReview}>Go to Settings →</button>
        </div>
      </div>
    );
  }

  const windowStart = new Date(); windowStart.setDate(windowStart.getDate() + 1); windowStart.setHours(0, 0, 0, 0);
  const windowEnd   = new Date(); windowEnd.setDate(windowEnd.getDate() + MAX_WINDOW_DAYS); windowEnd.setHours(23, 59, 59, 999);

  return (
    <div className="my-day-panels">
      {/* TODAY */}
      <div className="my-panel my-panel--today">
        <div className="my-panel__header">
          <span className="my-panel__title">Today</span>
          {myId && todayView === 'me' && <span className="my-panel__badge">({myId})</span>}
          <PanelToggle value={todayView} onChange={setTodayView} label="Today view" />
        </div>
        {loading && <PanelSpinner />}
        {!loading && error && <p className="my-panel__error">Could not load</p>}
        {!loading && !error && (todayView === 'me' ? (
          <TodayList
            events={todayEvents}
            familyEvents={familyToday}
            myId={myId}
            onChanged={onDataChanged}
            onViewFamily={() => setTodayView('family')}
          />
        ) : (
          <CappedEventList events={familyToday} variant="family" empty="Family is clear today" cap={cap} />
        ))}
      </div>

      {/* COMING UP */}
      <div className="my-panel my-panel--week">
        <div className="my-panel__header my-panel__header--stacked">
          <div className="my-panel__header-row">
            <span className="my-panel__title">Coming Up</span>
            <PanelToggle value={weekView} onChange={setWeekView} label="Coming up view" />
          </div>
          <hr className="my-panel__rule" />
          <div className="my-panel__daterange">
            <span className="my-panel__daterange-label">Date range</span>
            <span className="my-panel__subtitle">{fmtDateRange(windowStart, windowEnd)}</span>
          </div>
        </div>
        {loading && <PanelSpinner />}
        {!loading && error && <p className="my-panel__error">Could not load</p>}
        {!loading && !error && (weekView === 'me' ? (
          <CappedEventList
            events={upcomingEvents}
            empty={familyUpcoming.length > 0 ? 'Nothing assigned to you' : 'Nothing coming up'}
            emptyAction={familyUpcoming.length > 0 ? {
              label: `View ${familyUpcoming.length} family event${familyUpcoming.length !== 1 ? 's' : ''} →`,
              onClick: () => setWeekView('family'),
            } : null}
            groupByDay
            myId={myId}
            onChanged={onDataChanged}
            cap={cap}
          />
        ) : (
          <CappedEventList
            events={familyUpcoming}
            variant="family"
            groupByDay
            empty="Nothing for the family"
            cap={cap}
          />
        ))}
      </div>
    </div>
  );
}

// TODAY panel body: my events, or — if none — a pointer to the Family view
// so an empty personal day doesn't read as an empty family day.
function TodayList({ events, familyEvents, myId, onChanged, onViewFamily }) {
  if (events.length > 0) {
    return (
      <div className="my-panel__list">
        {events.map(e => <EventRow key={e.id} event={e} myId={myId} onChanged={onChanged} coverageMode="late" />)}
      </div>
    );
  }

  if (familyEvents.length === 0) {
    return <p className="my-panel__empty">Clear today</p>;
  }

  return (
    <div className="my-panel__list">
      <p className="my-panel__empty">Nothing assigned to you</p>
      <button className="my-panel__more" onClick={onViewFamily}>
        {familyEvents.length} family event{familyEvents.length !== 1 ? 's' : ''} — view →
      </button>
    </div>
  );
}

function FamilyRow({ event, dayLabel }) {
  const allDay = !event.start?.dateTime;
  const time   = allDay ? 'All day' : fmtTimeCompact(event.start.dateTime);
  const title  = stripAssigneeTag(event.summary);
  const isReminder = parseFOHMeta(event.description)?.reminder;

  return (
    <div className="family-row">
      {dayLabel !== undefined && <span className="today-row__day">{dayLabel}</span>}
      <span className="today-row__time">{time}</span>
      {event._calPersonName && (
        <span className="today-row__cal" style={{ color: event._calColor || undefined }}>
          {event._calPersonName}
        </span>
      )}
      <span className="today-row__title">{title}</span>
      <span className="family-row__assignee">
        {isReminder
          ? <ReminderIcon />
          : `(${event._assignee || '—'})`}
      </span>
    </div>
  );
}

function EventRow({ event, myId, onChanged, coverageMode = null, dayLabel }) {
  const allDay  = !event.start?.dateTime;
  const time    = allDay ? 'All day' : fmtTimeCompact(event.start.dateTime);
  const title   = stripAssigneeTag(event.summary);
  const [sheetOpen, setSheetOpen] = useState(false);

  const coverLabel = coverageMode === 'future'
    ? 'Ask someone to cover'
    : 'Running late? Request coverage';

  return (
    <>
      <div className="today-row">
        {dayLabel !== undefined && <span className="today-row__day">{dayLabel}</span>}
        <span className="today-row__time">{time}</span>
        {event._calPersonName && (
          <span className="today-row__cal" style={{ color: event._calColor || undefined }}>
            {event._calPersonName}
          </span>
        )}
        <span className="today-row__title">{title}</span>
        {coverageMode && (
          <button
            type="button"
            className="today-row__coverage-btn"
            onClick={() => setSheetOpen(true)}
            title={coverLabel}
            aria-label={coverLabel}
          >
            <CoverageIcon />
          </button>
        )}
      </div>
      {sheetOpen && (
        <CoverageRequestSheet
          event={event}
          myId={myId}
          mode={coverageMode}
          onClose={() => setSheetOpen(false)}
          onDone={() => { setSheetOpen(false); onChanged?.(); }}
        />
      )}
    </>
  );
}

// ─── REVIEW SCHEDULE BAR ──────────────────────────────────────────────────────
// Single entry point back into Review & Assign, replacing what used to be two
// separate This Week / Next Week cards. Most weeks both cards just showed a
// green checkmark — two full-width cards' worth of space to say "nothing to
// see" — so this collapses them into one icon + badge + one adaptive line,
// with this week's status always outranking next week's in both the number
// shown and the wording, matching how Review & Assign itself prioritizes.

// Short celebratory messages for the all-clear state — picked per week so
// they rotate week to week without flickering on re-render.
const ALL_CLEAR_MESSAGES = [
  'All set — nothing to sort',
  'Everything has a home',
  "You're on top of it",
  'Nothing needs you here',
  'All handled. Nice.',
  'Zero loose ends',
];

function allClearMessage(weekStart) {
  return ALL_CLEAR_MESSAGES[weekStart.getDate() % ALL_CLEAR_MESSAGES.length];
}

// Classify one week's events into conflict / unassigned / assigned — same
// logic Review & Assign itself uses. Derived synchronously from the shared
// calendar fetch (homeCal) instead of an independent per-week fetchEvents
// loop, so neither week triggers its own round of Google Calendar API calls.
function useWeekStatus(week, homeCal) {
  const { familyData } = useApp() || {};
  const { rawEvents: calEvents, ignoredMap, loading: calLoading, error: calError } = homeCal;

  const now       = new Date();
  const weekStart = week === 'this' ? startOfWeek(now) : nextStartOfWeek(now);
  const weekEnd   = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  const calAssignments = familyData?.config?.calendarAssignments || {};
  const suppressedCals = familyData?.config?.suppressedCalendars || [];
  const carePartners   = familyData?.carePartners || [];

  // Care partner calendar IDs — their personal calendars
  const cpCalIds = new Set(carePartners.map(cp => cp.calendarId).filter(Boolean));

  const status = useMemo(() => {
    if (calLoading || calError) return null;
    const calIds = Object.keys(calAssignments).filter(id => !suppressedCals.includes(id));
    if (calIds.length === 0) return { conflict: 0, unassigned: 0, assigned: 0 };

    const todayStr     = toLocalDateStr(now);
    const weekStartStr = toLocalDateStr(weekStart);
    const weekEndStr   = toLocalDateStr(weekEnd);

    const byDay = {};
    for (const e of calEvents) {
      if (!calIds.includes(e._calendarId)) continue;
      if (ignoredMap[e.id] || (e.recurringEventId && ignoredMap[e.recurringEventId])) continue;
      // Multi-day events overlap every day they span, not just their start
      // day — otherwise one that started before this window (or spans several
      // days within it) gets undercounted or dropped entirely.
      for (const d of eventDates(e)) {
        if (d < weekStartStr || d > weekEndStr) continue;
        // "This Week" only ever links to a Review range starting today (never
        // rewound to Monday) — skip already-past days so the badge can't count
        // something Review has no way to display.
        if (week === 'this' && d < todayStr) continue;
        if (!byDay[d]) byDay[d] = [];
        byDay[d].push(e);
      }
    }

    function isResolved(e) {
      const meta = parseFOHMeta(e.description);
      if (meta?.reminder || meta?.assignee) return true;
      if (/\([A-Z][A-Z\-\/]{0,5}\)\s*$/.test(e.summary || '')) return true;
      return false;
    }

    let conflict = 0, unassigned = 0, assigned = 0;
    for (const dayEvents of Object.values(byDay)) {
      const logistics = dayEvents.filter(e => !cpCalIds.has(e._calendarId));
      const cpWork    = dayEvents.filter(e => cpCalIds.has(e._calendarId) && !isResolved(e));
      const unresolved = logistics.filter(e => !isResolved(e));

      if (unresolved.length > 0 && cpWork.length > 0) { conflict++; }
      else if (unresolved.length > 0)                  { unassigned++; }
      else                                              { assigned++; }
    }

    return { conflict, unassigned, assigned };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calEvents, ignoredMap, calLoading, calError, week]);

  return { weekStart, status, loading: status === null && !calError, error: !!calError };
}

function ReviewScheduleBar({ onTap, homeCal }) {
  const thisWeek = useWeekStatus('this', homeCal);
  const nextWeek = useWeekStatus('next', homeCal);

  const loading = thisWeek.loading || nextWeek.loading;
  const error   = thisWeek.error || nextWeek.error;

  const thisIssues  = thisWeek.status ? thisWeek.status.conflict + thisWeek.status.unassigned : 0;
  const nextIssues  = nextWeek.status ? nextWeek.status.conflict + nextWeek.status.unassigned : 0;
  const totalIssues = thisIssues + nextIssues;
  const allClear    = !loading && !error && totalIssues === 0;

  // This week always outranks next week, both in which count leads the line
  // and which week tapping the bar jumps into.
  let line = null, tapWeek = 'this', tapFilter = null;
  if (!loading && !error) {
    if (thisWeek.status?.conflict > 0) {
      line = <><b>{thisWeek.status.conflict} conflict{thisWeek.status.conflict !== 1 ? 's' : ''}</b> this week</>;
      tapFilter = 'conflict';
    } else if (thisWeek.status?.unassigned > 0) {
      line = <><b>{thisWeek.status.unassigned} unassigned</b> this week</>;
      tapFilter = 'unassigned';
    } else if (nextWeek.status?.conflict > 0) {
      line = <><b>{nextWeek.status.conflict} conflict{nextWeek.status.conflict !== 1 ? 's' : ''}</b> next week</>;
      tapFilter = 'conflict'; tapWeek = 'next';
    } else if (nextWeek.status?.unassigned > 0) {
      line = <><b>{nextWeek.status.unassigned} unassigned</b> next week</>;
      tapFilter = 'unassigned'; tapWeek = 'next';
    } else {
      line = <b>{allClearMessage(thisWeek.weekStart)}</b>;
    }
  }

  return (
    <div className={`review-schedule-bar ${allClear ? 'review-schedule-bar--clear' : ''}`}>
      <button
        type="button"
        className="review-schedule-bar__button"
        onClick={() => onTap(tapFilter, tapWeek)}
      >
        <span className="review-schedule-bar__icon" aria-hidden="true">🔍</span>
        <span className="review-schedule-bar__label">Review Schedule</span>
        {!loading && !error && (
          <span className="review-schedule-bar__badge">{totalIssues}</span>
        )}
      </button>
      <div className="review-schedule-bar__line">
        {loading && '…'}
        {error && 'Could not load'}
        {!loading && !error && line}
      </div>
    </div>
  );
}

// CappedEventList — shows up to `cap` events chronologically, +N more if overflow
// groupByDay=true groups by date header, capping by whole days (for This Week panel)
// variant="family" renders read-only FamilyRows (assignee chip, no Running-late sheet)
function CappedEventList({ events, empty, emptyAction, groupByDay = false, variant = 'me', myId, onChanged, coverageMode = null, cap = 5 }) {
  // Sort chronologically
  const sorted = [...events].sort((a, b) =>
    (a.start?.dateTime || a.start?.date || '').localeCompare(
     (b.start?.dateTime || b.start?.date || ''))
  );

  // `day` (when given) makes the row's key unique across repeated
  // multi-day events; `dayLabel` (when given, even as '') renders the
  // folded-in day column instead of leaving it out entirely.
  const renderRow = (e, day, dayLabel) => {
    const key = day ? `${day}-${e.id}` : e.id;
    return variant === 'family'
      ? <FamilyRow key={key} event={e} dayLabel={dayLabel} />
      : <EventRow key={key} event={e} myId={myId} onChanged={onChanged} coverageMode={coverageMode} dayLabel={dayLabel} />;
  };

  if (sorted.length === 0) {
    return (
      <>
        <p className="my-panel__empty">{empty}</p>
        {emptyAction && (
          <button className="my-panel__more" onClick={emptyAction.onClick}>{emptyAction.label}</button>
        )}
      </>
    );
  }

  if (!groupByDay) {
    const visible  = sorted.slice(0, cap);
    const overflow = sorted.length - cap;
    return (
      <div className="my-panel__list">
        {visible.map(e => renderRow(e))}
        {overflow > 0 && (
          <p className="my-panel__overflow">+{overflow} more</p>
        )}
      </div>
    );
  }

  // Group the FULL list by day, then include whole days up to `cap` items —
  // never truncate a day partway through (a day missing one of its own events
  // reads as a bug, not "more"). Always show at least the first day even if
  // it alone exceeds cap, so a single busy day doesn't render empty.
  // Multi-day events repeat under every day they cover, not just their start
  // day, so a several-day trip shows up on each of its days here too — except
  // today (or earlier), which belongs to the Today panel, not Coming Up.
  const today = toLocalDateStr(new Date());
  const byDay = {};
  const dayOrder = [];
  for (const e of sorted) {
    for (const d of eventDates(e)) {
      if (d <= today) continue;
      if (!byDay[d]) { byDay[d] = []; dayOrder.push(d); }
      byDay[d].push(e);
    }
  }
  dayOrder.sort();

  const shownDays = [];
  const shownEventIds = new Set();
  let shownCount = 0;
  for (const d of dayOrder) {
    if (shownCount > 0 && shownCount + byDay[d].length > cap) break;
    shownDays.push(d);
    shownCount += byDay[d].length;
    for (const e of byDay[d]) shownEventIds.add(e.id);
  }

  // Unique events, not rows — a multi-day event shown on 3 days is 1 event,
  // not 3, for "+N more" purposes.
  const overflow = sorted.length - shownEventIds.size;

  // No separate header bar — the day folds into the row itself (blank on
  // every row after a day's first), the same way the time column already
  // works. Removes the repeating header/gap rhythm a sparse, many-day list
  // used to have.
  return (
    <div className="week-groups">
      {shownDays.flatMap(day =>
        byDay[day].map((e, i) => renderRow(e, day, i === 0 ? fmtDayDate(day) : ''))
      )}
      {overflow > 0 && (
        <p className="my-panel__overflow">+{overflow} more</p>
      )}
    </div>
  );
}

// Me | Family segmented pill for the Today / Coming Up panel headers
function PanelToggle({ value, onChange, label }) {
  return (
    <div className="my-panel__toggle" role="group" aria-label={label}>
      {[['me', 'Me'], ['family', 'Family']].map(([v, text]) => (
        <button
          key={v}
          type="button"
          className={`my-panel__toggle-btn${value === v ? ' my-panel__toggle-btn--active' : ''}`}
          aria-pressed={value === v}
          onClick={() => onChange(v)}
        >{text}</button>
      ))}
    </div>
  );
}

function PanelSpinner() {
  return (
    <div className="my-panel__loading">
      <span className="my-panel__spinner" />
    </div>
  );
}
